import { useEffect, useState, useCallback } from "react";
import { MessageCircle, Save, Send, RefreshCw, Copy, Play, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import WebhookEvents from "@/components/WebhookEvents";
import { GlobalAutomationCard } from "@/components/whatsapp/GlobalAutomationCard";
import { SetupChecklist } from "@/components/whatsapp/SetupChecklist";
import { TestResult } from "@/components/whatsapp/TestResult";
import { AiAgentPrompt } from "@/components/whatsapp/AiAgentPrompt";
import { ProviderSelect } from "@/components/whatsapp/ProviderFields";
import { useNouvelleTemplates, NouvelleTemplateSelect, NouvelleSyncBar } from "@/components/whatsapp/NouvelleTemplates";
import { HandoffsPanel } from "@/components/whatsapp/HandoffsPanel";
import { AiFaqCard } from "@/components/whatsapp/AiFaqCard";
import { InboundWeeklyStats } from "@/components/whatsapp/InboundWeeklyStats";
import { ConversationThreads } from "@/components/whatsapp/ConversationThreads";
import { ConversationExport } from "@/components/whatsapp/ConversationExport";
import { SettingsHistory } from "@/components/whatsapp/SettingsHistory";
import { SimResult } from "@/components/whatsapp/SimResult";

const OUTCOMES = [
  ["win", "Vincita", "CODICE, PREMIO, SERIALE, CONCORSO, SEDE, SCADENZA, LINK_RICEVUTA"],
  ["receipt", "Ricevuta PDF (template con header Documento)", "PREMIO, SERIALE, CONCORSO"],
  ["lose", "Non vincita", "CODICE, CONCORSO"],
  ["used", "Codice già utilizzato", "CODICE, CONCORSO"],
  ["invalid", "Codice inesistente / non valido", "CODICE"],
  ["invalid_suggest", "Codice con refuso → suggerimento del codice più simile", "CODICE, SUGGERIMENTO"],
  ["invalid_ambiguous", "Codice con refuso simile a più codici → invito a ricontrollare", "CODICE"],
  ["photo_unreadable", "Foto dello scontrino ricevuta ma codice illeggibile", "—"],
  ["photo_multi", "Foto con più codici → chiedi quale giocare", "CODICI, QUANTI"],
  ["customer_rank", "Classifica mensile clienti (top 5, il 1° del mese)", "MESE, POSIZIONE, GIOCATE, VINCITE, CONCORSO, SCONTO, SCONTO_TESTO"],
  ["discount_reminder", "Promemoria sconto classifica in scadenza (5 giorni prima)", "SCONTO, PREMIO, SCADENZA"],
  ["welcome_contest", "Benvenuto dopo iscrizione dalla landing del concorso (se attivo nel tab Landing)", "CONCORSO, NOME, COME, PREMIO_TESTO, LINK"],
  ["signup_reminder", "Promemoria premio di benvenuto in scadenza (5 giorni prima)", "SCONTO, PREMIO, SCADENZA"],
  ["signup_last_call", "Ultimo avviso premio di benvenuto (il giorno prima della scadenza)", "SCONTO, PREMIO, SCADENZA"],
  ["discount_redeemed", "Conferma sconto classifica riscattato in negozio", "SCONTO, PREMIO, SEDE, DATA"],
  ["wrong_channel", "Codice di un concorso totem (non WhatsApp)", "CODICE, CONCORSO"],
  ["wrong_store_alert", "Avviso all'admin: sede oltre la soglia di tentativi fuori sede", "SEDE, TENTATIVI, SOGLIA, DATA, DETTAGLIO, CONCORSO"],
  ["temp_pin", "PIN temporaneo inviato alla sede (PIN dimenticato)", "SEDE, PIN"],
  ["pin_expiry", "Promemoria alla sede: PIN in scadenza / scaduto (90 giorni)", "SEDE, GIORNI, STATO"],
  ["unrecognized", "Messaggio non riconosciuto (solo con interruttore globale acceso)", "ESEMPIO"],
  ["ai_reply", "Risposta dell'agente IA integrato (testo libero)", "RISPOSTA"],
  ["closed", "Concorso terminato", "CODICE, CONCORSO"],
  ["not_started", "Concorso non ancora iniziato", "CODICE, CONCORSO, INIZIO"],
  ["otp", "Codice OTP di ritiro", "OTP, PREMIO, SEDE, MINUTI"],
  ["reminder", "Promemoria scadenza ricevuta", "PREMIO, SERIALE, SCADENZA, CONCORSO"],
  ["rules", "REGOLAMENTO: testo + contatti (PDF allegato se presente)", "CONCORSO, REGOLAMENTO, CONTATTI"],
  ["rules_missing", "REGOLAMENTO: nessun regolamento disponibile", "CONCORSO"],
  ["rules_updated", "Avviso nuova versione regolamento", "CONCORSO, VERSIONE, LINK_REGOLAMENTO"],
  ["no_prize", "RITIRO: nessun premio da ritirare", "CONCORSO, SERIALE"],
  ["choose_prize", "RITIRO: più premi, scegli il seriale", "PREMI, SERIALI"],
  ["consent_ok", "Consenso marketing ricevuto (risposta SI)", "CONCORSO"],
  ["consent_revoked", "Consenso revocato (risposta STOP)", "CONCORSO"],
  ["campaign", "Campagna promozionale", "TESTO, NOME_CONCORSO, PREMIO"],
];
const outcomeStyle = {
  non_partecipante: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  ai_reply: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  operator_wait: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  wrong_channel: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  win: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  lose: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  used: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  invalid: "bg-red-500/15 text-red-400 border-red-500/30",
  closed: "bg-red-500/15 text-red-300 border-red-500/30",
  not_started: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  otp: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  receipt: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  reminder: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  no_prize: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  choose_prize: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  consent_ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  consent_revoked: "bg-red-500/15 text-red-300 border-red-500/30",
  campaign: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
};

export default function WhatsAppTab({ contest, onSaved }) {
  const [cfg, setCfg] = useState({ templates: {}, language: "IT" });
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [sim, setSim] = useState({ phone: "+393331234567", text: "" });
  const [simResult, setSimResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [grouped, setGrouped] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [checkKey, setCheckKey] = useState(0);
  const [globalProvider, setGlobalProvider] = useState("spoki");
  useEffect(() => { api.waSettings().then((s) => setGlobalProvider(s.provider || "spoki")).catch(() => {}); }, [checkKey]);
  const effectiveProvider = cfg.provider || globalProvider;
  const nouvelle = useNouvelleTemplates(effectiveProvider === "nouvelle");

  const testTemplate = async (outcome) => {
    if (!/^\+\d{8,15}$/.test(testPhone.trim())) return toast.error("Inserisci il numero di prova in formato internazionale (+39...)");
    setTesting(outcome);
    try {
      const r = await api.whatsappTest(contest.id, testPhone.trim(), outcome);
      setTestResults((t) => ({ ...t, [outcome]: { ...r, outcome } }));
      if (r.status === "failed" || r.status === "skipped") toast.error(`${outcome}: ${r.error || "errore Spoki"}`);
      else toast.success(r.mock ? `Template "${outcome}" simulato (vedi log)` : `Template "${outcome}" inviato a ${testPhone}`);
      load();
    } catch (e) {
      const err = e?.response?.data?.detail || "Test non riuscito";
      setTestResults((t) => ({ ...t, [outcome]: { status: "failed", error: err, outcome } }));
      toast.error(err);
    } finally {
      setTesting(null);
    }
  };

  useEffect(() => {
    setCfg({ provider: contest.whatsapp?.provider || null, templates: contest.whatsapp?.templates || {}, secrets: {}, has_secrets: Object.keys(contest.whatsapp?.secrets || {}), language: contest.whatsapp?.language || "IT", automation_url: contest.whatsapp?.automation_url || "", automation_secret: "", has_secret: !!contest.whatsapp?.automation_secret });
  }, [contest]);
  const load = useCallback(() => Promise.all([api.whatsappMessages(contest.id).then(setData), api.whatsappSummary(contest.id).then(setSummary)]).catch(() => {}), [contest.id]);
  const retryNow = async () => {
    setRetrying(true);
    try { const r = await api.whatsappRetryNow(); toast.success(`Controllati ${r.checked}, reinviati ${r.resent}, ancora falliti ${r.still_failed}`); load(); } catch { toast.error("Reinvio non riuscito"); } finally { setRetrying(false); }
  };
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    await api.setWhatsappConfig(contest.id, cfg);
    toast.success("Configurazione WhatsApp salvata");
    setCheckKey((k) => k + 1);
    onSaved?.();
  };
  const simulate = async () => {
    if (!sim.text.trim()) return toast.error("Inserisci il testo da inviare");
    setBusy(true);
    try {
      const r = await api.whatsappSimulate(sim.phone, sim.text);
      setSimResult({ ...r, text: sim.text });
      setSim((s) => ({ ...s, text: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="whatsapp-tab">
      <div className="lg:col-span-2 space-y-4">
        <SetupChecklist contestId={contest.id} refreshKey={checkKey} />
        <Card className="p-5 bg-card border-border space-y-3">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Collegamento Spoki</h3>
          {data && (
            <div className={`rounded-lg p-3 text-xs border ${data.mock ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`} data-testid="whatsapp-mode">
              {data.mock ? "Modalità SIMULAZIONE: le risposte vengono registrate ma non inviate. Inserisci SPOKI_API_KEY per attivare l'invio reale." : "Invio reale attivo tramite Spoki."}
            </div>
          )}
          {summary && (
            <div className="rounded-lg border border-border p-3 space-y-2" data-testid="whatsapp-summary">
              <div className="flex items-center justify-between text-xs"><span className="font-semibold">Riepilogo consegne</span><span className="text-muted-foreground">{summary.total} messaggi · {summary.retried} reinviati</span></div>
              <div className="grid grid-cols-4 gap-1.5 text-center">
                {[["Inviati", summary.dispatched, summary.rates.dispatched, "text-emerald-400", "whatsapp-summary-sent"], ["Consegnati", summary.counts.delivered + summary.counts.read, summary.rates.delivered, "text-teal-300", "whatsapp-summary-delivered"], ["Letti", summary.counts.read, summary.rates.read, "text-sky-300", "whatsapp-summary-read"], ["Falliti", summary.counts.failed, summary.rates.failed, "text-red-400", "whatsapp-summary-failed"]].map(([l, n, p, cls, tid]) => (
                  <div key={l} className="rounded-md bg-secondary/40 p-2" data-testid={tid}><p className={`font-display font-bold text-lg leading-none ${cls}`}>{n}</p><p className="text-[10px] text-muted-foreground">{l} · {p}%</p></div>
                ))}
              </div>
              <div className="flex h-1.5 rounded-full overflow-hidden bg-secondary/40">
                {summary.total > 0 && <><div className="bg-sky-400" style={{ width: `${(summary.counts.read / summary.total) * 100}%` }} /><div className="bg-teal-400" style={{ width: `${(summary.counts.delivered / summary.total) * 100}%` }} /><div className="bg-emerald-500" style={{ width: `${(summary.counts.sent / summary.total) * 100}%` }} /><div className="bg-red-500" style={{ width: `${(summary.counts.failed / summary.total) * 100}%` }} /></>}
              </div>
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>I falliti vengono reinviati automaticamente dopo 1 ora (max 2 tentativi)</span>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" disabled={retrying} onClick={retryNow} data-testid="whatsapp-retry-now">{retrying ? "…" : "Reinvia ora"}</Button>
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">URL webhook da configurare in Spoki (Integrazioni → Webhook, evento messaggio ricevuto)</Label>
            <div className="flex gap-2">
              <Input readOnly value={data?.webhook_url || ""} className="h-8 text-xs font-mono-code" data-testid="whatsapp-webhook-url" />
              <Button size="sm" variant="secondary" className="h-8" onClick={() => { navigator.clipboard.writeText(data?.webhook_url || ""); toast.success("Copiato"); }}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </Card>

        <GlobalAutomationCard onSaved={() => setCheckKey((k) => k + 1)} />
        <HandoffsPanel />
        <AiFaqCard />
        <SettingsHistory refreshKey={checkKey} onRestored={() => setCheckKey((k) => k + 1)} />
        <AiAgentPrompt />
        <InboundWeeklyStats />

        <Card className="p-5 bg-card border-border space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-lg">Template Spoki per esito</h3>
            <Button size="sm" onClick={save} className="gap-1.5" data-testid="whatsapp-save"><Save className="h-3.5 w-3.5" /> Salva</Button>
          </div>
          <p className="text-xs text-muted-foreground">Per ciascun esito (vincita, non vincita, promemoria…) inserisci l'ID del template approvato in Spoki <b>oppure</b> l'URL di un'automazione Spoki dedicata (https://api.spoki.com/wh/ap/…) con il suo secret. Se lasci vuoto un esito, viene usata l'automazione di questo concorso qui sotto, altrimenti quella globale.</p>
          <div className="rounded-lg bg-secondary/40 p-3 space-y-2" data-testid="whatsapp-automation-box">
            <Label className="text-xs">Servizio WhatsApp per questo concorso</Label>
            <ProviderSelect value={cfg.provider || null} onChange={(v) => setCfg({ ...cfg, provider: v })} allowInherit testId="whatsapp-contest-provider" />
            <Label className="text-xs">Automazione Spoki di questo concorso (URL) — vuoto = usa quella globale</Label>
            <Input value={cfg.automation_url || ""} onChange={(e) => setCfg({ ...cfg, automation_url: e.target.value.trim() })} placeholder="https://api.spoki.com/wh/ap/…/" className="h-8 text-xs font-mono-code" data-testid="whatsapp-automation-url" />
            <Label className="text-xs">Secret automazione (dal dettaglio automazione in Spoki)</Label>
            <Input type="password" value={cfg.automation_secret || ""} onChange={(e) => setCfg({ ...cfg, automation_secret: e.target.value.trim() })} placeholder={cfg.has_secret ? "•••••••• (già impostato, lascia vuoto per mantenerlo)" : "secret"} className="h-8 text-xs font-mono-code" data-testid="whatsapp-automation-secret" />
            <p className="text-[11px] text-muted-foreground">L'automazione riceve i campi dinamici (es. CODICE, PREMIO, SERIALE) più TESTO (messaggio completo) e DOCUMENT_URL (PDF).</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Lingua template</Label>
            <Input value={cfg.language} onChange={(e) => setCfg({ ...cfg, language: e.target.value.toUpperCase() })} className="h-8 w-24" data-testid="whatsapp-language" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Numero di prova (per il tasto ▶ accanto a ogni template)</Label>
            <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+393331234567" className="h-8" data-testid="whatsapp-test-phone" />
          </div>
          {effectiveProvider === "nouvelle" && <NouvelleSyncBar synced_at={nouvelle.synced_at} count={nouvelle.templates.length} onSynced={nouvelle.reload} />}
          {OUTCOMES.map(([k, l, f]) => {
            const isUrl = String(cfg.templates[k] ?? "").startsWith("http");
            return (
            <div key={k} className="border-t border-border pt-2 space-y-1.5">
              <div className="grid grid-cols-[1fr_150px_32px] gap-2 items-center">
                <div>
                  <div className="text-sm">{l}</div>
                  <div className="text-[10px] text-muted-foreground font-mono-code">{f}</div>
                </div>
                {effectiveProvider === "nouvelle"
                  ? <NouvelleTemplateSelect value={cfg.templates[k] ?? ""} onChange={(v) => setCfg({ ...cfg, templates: { ...cfg.templates, [k]: v } })} templates={nouvelle.templates} testId={`whatsapp-nouvelle-template-${k}`} />
                  : <Input placeholder="ID o URL" value={cfg.templates[k] ?? ""} onChange={(e) => setCfg({ ...cfg, templates: { ...cfg.templates, [k]: e.target.value } })} className="h-8 text-xs" data-testid={`whatsapp-template-${k}`} />}
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Invia un test" disabled={testing !== null} onClick={() => testTemplate(k)} data-testid={`whatsapp-test-${k}`}>
                  {testing === k ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <TestResult r={testResults[k]} />
              {isUrl && (
                <Input type="password" value={cfg.secrets?.[k] ?? ""} onChange={(e) => setCfg({ ...cfg, secrets: { ...cfg.secrets, [k]: e.target.value } })}
                  placeholder={cfg.has_secrets?.includes(k) ? "•••••••• secret automazione (già impostato)" : "Secret di questa automazione (vuoto = usa quello predefinito)"} className="h-7 text-xs font-mono-code" data-testid={`whatsapp-secret-${k}`} />
              )}
            </div>
            );
          })}
        </Card>
        <WebhookEvents />
      </div>

      <div className="lg:col-span-3 space-y-4">
        <Card className="p-5 bg-card border-border space-y-3">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Send className="h-4 w-4" /> Simula messaggio in arrivo</h3>
          <p className="text-xs text-muted-foreground">Simula un cliente che invia un codice al numero WhatsApp: il sistema lo verifica (inesistente, già giocato, concorso chiuso…) e produce la risposta.</p>
          <div className="grid grid-cols-[170px_1fr_auto] gap-2">
            <Input value={sim.phone} onChange={(e) => setSim({ ...sim, phone: e.target.value })} placeholder="+39..." data-testid="whatsapp-sim-phone" />
            <Input value={sim.text} onChange={(e) => setSim({ ...sim, text: e.target.value })} placeholder="Codice, comando o frase generica (es. 'che orari fate?')" data-testid="whatsapp-sim-text" onKeyDown={(e) => e.key === "Enter" && simulate()} />
            <Button onClick={simulate} disabled={busy} data-testid="whatsapp-sim-send">Invia</Button>
          </div>
          <SimResult r={simResult} />
        </Card>

        <Card className="bg-card border-border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-semibold text-sm">Conversazioni WhatsApp</h3>
            <div className="flex items-center gap-1">
              <Button size="sm" variant={grouped ? "secondary" : "ghost"} className="h-7 text-xs gap-1" onClick={() => setGrouped((g) => !g)} data-testid="whatsapp-group-toggle"><Users className="h-3.5 w-3.5" /> {grouped ? "Per cliente" : "Cronologico"}</Button>
              <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <ConversationExport contestId={contest.id} />
          {(!data || data.messages.length === 0) && (
            <p className="p-6 text-sm text-muted-foreground text-center" data-testid="whatsapp-empty">Nessun messaggio ancora.</p>
          )}
          {data?.messages.length > 0 && grouped && <ConversationThreads messages={data.messages} outcomeStyle={outcomeStyle} />}
          {data?.messages.length > 0 && !grouped && (
            <ul className="divide-y divide-border max-h-[520px] overflow-auto">
              {data.messages.map((m) => (
                <li key={m.id} className="p-3 text-sm space-y-1" data-testid={`whatsapp-msg-${m.id}`}>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono-code">{new Date(m.created_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}</span>
                    <span className="font-semibold text-foreground">{m.phone}</span>
                    <Badge className={`border ${outcomeStyle[m.outcome] || ""}`}>{m.outcome}</Badge>
                    {m.mock && <span className="text-amber-400">simulato</span>}
                    {m.status === "failed" && <span className="text-red-400">invio fallito{m.error ? `: ${String(m.error).slice(0, 80)}` : ""}</span>}
                    {m.source === "ritiro" && <span>ritiro</span>}
                    {m.source === "test" && <span className="text-sky-400">test</span>}
                    {m.source === "anteprima" && <span className="text-sky-400">anteprima campagna</span>}
                    {m.source === "campagna" && <span className="text-fuchsia-400">campagna</span>}
                    {m.source === "totem" && <span>da totem</span>}
                    {m.source === "promemoria" && <span>promemoria</span>}
                  </div>
                  {m.inbound_text && <p className="text-xs"><span className="text-muted-foreground">Cliente:</span> <span className="font-mono-code">{m.inbound_text}</span></p>}
                  <p className="text-xs rounded-md bg-emerald-500/10 border border-emerald-500/20 px-2 py-1.5 whitespace-pre-wrap">{m.preview}</p>
                  {m.document_url && <a href={m.document_url} target="_blank" rel="noreferrer" className="text-xs underline text-emerald-400" data-testid={`whatsapp-doc-${m.id}`}>📄 Allegato: ricevuta PDF</a>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
